/**
 * Blok level generator: level N -> { seed, theme, picture index, bag weights, obstacles, fairness }.
 * `generateLevel(n)` is cheap and deterministic. Difficulty is *graded* by the bot (bot.ts,
 * gradeLevel) — the tests assert every level 1..30 grades as playable (not "broken") and that
 * levels 1-3 are friendly.
 *
 * Curve (d = clamp((n-1)/24, 0, 1)):
 *  - weights: small pieces fade out, 4/5-cell shapes fade in (I5 from L6, big rectangles L6+, 3x3 L10+)
 *  - obstacles: none before L6, then +1 per ~1.4 levels up to 14 pre-filled cells
 *  - fairness: levels 1-3 insist on fully placeable trios (24 tries), 4-10: 10, 11-20: 5, 21+: 3
 *
 * Difficulty bands (bot.ts gradeLevel; same idea as Mozaik, measured by bot survival to 40% reveal):
 *   easy    survival >= 0.95
 *   medium  survival >= 0.80
 *   hard    survival >= 0.50
 *   expert  survival in [0.30, 0.50)
 *   broken  survival  < 0.30   -> must be re-tuned (a level that kills the bot is not fun)
 *
 * From level 21 on the chapter designer (@enjoyment/core designer.ts) picks which ramp level (10–31) each level
 * plays like and the chapter's twist; seed, theme and picture still follow the real level number.
 */
import { designLevel, hashSeed, Rng, type DesignSpec, type Picture } from "@enjoyment/core";
import { PIECES, SIDE } from "./pieces";
import { CELLS, type BlokLevel } from "./game";

/** Theme order of the cycle (level N uses THEME_CYCLE[(N-1) % length]). */
export const THEME_CYCLE = ["animals", "food", "flags", "vehicles", "nature", "objects", "music-sport"] as const;

const clamp01 = (x: number) => Math.max(0, Math.min(1, x));

/**
 * Chapter design (levels 21+). Floor 10: every piece family is in the bag (3x3 from L10), obstacles are on.
 * Ceiling 31: the last ramp step (bag ~L25, obstacles L22, a single fair-deal try from L31). Twists are knobs the
 * ramp already has: a chapter whose bag leans on straight bars (`lines`), on squares and rectangles (`blocks`), on
 * T/S/Z shapes (`zigzag`), or with 3 more obstacles, up to 14 (`rocks`). Every twist also gets one more fair-deal
 * try (rocks two), so a twisted chapter starts as safely as the plain ramp (bot check: no early death in 21–200).
 */
export const DESIGN: DesignSpec = { game: "blok", floor: 10, ceiling: 31, twists: ["lines", "blocks", "zigzag", "rocks"] };

/** Bag twists: the piece families a chapter leans on and their weight factor (S/Z/T are the awkward ones). */
const BAG_TWISTS: Record<string, { families: readonly string[]; factor: number }> = {
  lines: { families: ["i2", "i3", "i4", "i5"], factor: 2 },
  blocks: { families: ["o2", "r23", "sq3"], factor: 2 },
  zigzag: { families: ["t4", "s4"], factor: 1.5 },
};
/** Obstacle cap of the `rocks` twist (the ramp's own cap is 12). */
export const MAX_ROCKS = 14;

/** Bag weights of the hand-tuned ramp at level n (levels 1–20, and the scale the designer draws from). */
export function bagWeights(n: number): Record<string, number> {
  const d = clamp01((n - 1) / 24);
  const w: Record<string, number> = {};
  const add = (ids: string[], v: number) => {
    if (v <= 0.001) return;
    for (const id of ids) w[id] = Math.round(v * 100) / 100;
  };
  add(["dot"], 3 - 2.4 * d);
  add(["i2h", "i2v"], 3 - 1.8 * d);
  add(["i3h", "i3v"], 3 - 1.2 * d);
  add(["c3a", "c3b", "c3c", "c3d"], 3 - 0.9 * d);
  add(["o2"], 4 - 2.0 * d);
  add(["i4h", "i4v"], n < 2 ? 0 : 0.8 + 1.6 * d);
  add(["t4a", "t4b", "t4c", "t4d"], n < 3 ? 0 : 1 + 1.2 * d);
  add(["l4a", "l4b", "l4c", "l4d", "j4a", "j4b", "j4c", "j4d"], n < 4 ? 0 : 0.8 + 1.0 * d);
  add(["s4a", "s4b", "z4a", "z4b"], n < 4 ? 0 : 0.7 + 0.9 * d);
  add(["i5h", "i5v"], n < 6 ? 0 : 0.5 + 1.1 * d);
  add(["r23h", "r23v"], n < 6 ? 0 : 0.5 + 1.0 * d);
  add(["l5a", "l5b", "l5c", "l5d"], n < 8 ? 0 : 0.5 + 1.2 * d);
  add(["sq3"], n < 10 ? 0 : 0.4 + 1.0 * d);
  return w;
}

/** Obstacles of the hand-tuned ramp at level n. */
export function obstacleCount(n: number): number {
  return n < 6 ? 0 : Math.min(12, 2 + Math.floor((n - 6) / 1.6));
}

/** Seeded obstacle cells: never a complete line, at most 3 per row/column, no touching clumps of 4. */
export function obstacleCells(n: number, seed: number, want = obstacleCount(n)): number[] {
  const rng = new Rng(seed ^ 0x0b57ac1e);
  const out: number[] = [];
  const rowN = new Array<number>(SIDE).fill(0);
  const colN = new Array<number>(SIDE).fill(0);
  let guard = 0;
  while (out.length < want && guard++ < 500) {
    const i = rng.int(CELLS);
    const r = Math.floor(i / SIDE);
    const c = i % SIDE;
    if (out.includes(i) || rowN[r]! >= 3 || colN[c]! >= 3) continue;
    out.push(i);
    rowN[r]!++;
    colN[c]!++;
  }
  return out.sort((a, b) => a - b);
}

/** Fair-deal tries of the hand-tuned ramp at level n. */
export function fairTriesFor(n: number): number {
  return n <= 3 ? 24 : n <= 8 ? 10 : n <= 15 ? 5 : n <= 22 ? 3 : n <= 30 ? 2 : 1;
}

export function generateLevel(n: number): BlokLevel {
  const level = Math.max(1, Math.floor(n));
  const seed = hashSeed(`blok:level:${level}`);
  // Levels 1–20 are the ramp itself; from 21 on the difficulty comes from the ramp level the designer picks.
  const d = designLevel(DESIGN, level);
  const ramp = d ? d.effective : level;
  let weights = bagWeights(ramp);
  let obstacles = obstacleCount(ramp);
  let fairTries = fairTriesFor(ramp);
  if (d?.twist) {
    const bag = BAG_TWISTS[d.twist];
    if (bag) weights = boostFamilies(weights, bag.families, bag.factor);
    if (d.twist === "rocks") obstacles = Math.min(MAX_ROCKS, obstacles + 3);
    fairTries += d.twist === "rocks" ? 2 : 1;
  }
  return {
    n: level,
    seed,
    theme: THEME_CYCLE[(level - 1) % THEME_CYCLE.length]!,
    pictureIndex: Math.floor((level - 1) / THEME_CYCLE.length),
    weights,
    obstacles: obstacleCells(ramp, seed, obstacles),
    fairTries,
  };
}

/** Multiplies the weight of the given piece families (pieces not in the bag stay out). */
function boostFamilies(weights: Record<string, number>, families: readonly string[], factor: number): Record<string, number> {
  const out: Record<string, number> = {};
  for (const [id, w] of Object.entries(weights)) out[id] = families.includes(PIECES[id]!.family) ? Math.round(w * factor * 100) / 100 : w;
  return out;
}

/** Pictures of the level's theme in a stable order (the answer-suggestion pool too). */
export function themePool(level: BlokLevel, pictures: readonly Picture[]): Picture[] {
  const pool = pictures.filter((p) => p.theme === level.theme);
  return (pool.length ? pool : [...pictures]).sort((a, b) => (a.id < b.id ? -1 : a.id > b.id ? 1 : 0));
}

/**
 * The level's picture: the theme pool is shuffled with a theme-only seed, and the k-th visit of
 * the theme takes the k-th picture of that order — so a theme never repeats a picture until the
 * whole pool has been used, and everyone sees the same picture at the same level.
 */
export function pickPicture(level: BlokLevel, pictures: readonly Picture[]): Picture {
  const pool = themePool(level, pictures);
  if (!pool.length) throw new Error("empty picture pool");
  const rng = new Rng(hashSeed(`blok:theme:${level.theme}`));
  const order = pool.map((_, i) => i);
  for (let i = order.length - 1; i > 0; i--) {
    const j = rng.int(i + 1);
    [order[i], order[j]] = [order[j]!, order[i]!];
  }
  return pool[order[level.pictureIndex % order.length]!]!;
}
